import React, { useReducer, useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Save, Rocket, Check, AlertCircle, Loader2 } from 'lucide-react';

import { Button, Card, Stepper, PageHeader, WizardFooter } from '../../ui-v2';
import {
  wizardReducer,
  initialState,
  buildPayload,
  pasoEsValidoLocal,
  TOTAL_PASOS,
} from '../wizard/wizardState';
import {
  createCampana,
  updateCampana,
  cambiarEstadoCampana,
  getCampanaById,
} from '../api';

import Paso1Info from '../wizard/Paso1Info';
import Paso2Subtareas from '../wizard/Paso2Subtareas';
import Paso3Fechas from '../wizard/Paso3Fechas';
import Paso4DiasDisponibles from '../wizard/Paso4DiasDisponibles';
import Paso5Tecnicos from '../wizard/Paso5Tecnicos';
import Paso6Usuarios from '../wizard/Paso6Usuarios';
import Paso7Verificacion from '../wizard/Paso7Verificacion';
import { InformacionDosColumnas, BloqueReglas } from '../wizard/BloquesCampana';

const PASOS = [
  { num: 1, titulo: 'Información' },
  { num: 2, titulo: 'Subtareas' },
  { num: 3, titulo: 'Fechas y horario' },
  { num: 4, titulo: 'Días disponibles' },
  { num: 5, titulo: 'Técnicos' },
  { num: 6, titulo: 'Usuarios' },
  { num: 7, titulo: 'Verificación' },
];

const DESCRIPCION_PASO = [
  'Ámbito, cómo se llama por dentro, lo que ve la persona y las reglas.',
  'Listado de subtareas a marcar en cada cita (opcional).',
  'Fechas de inicio y límite + horario de las citas.',
  'Días disponibles dentro del rango.',
  'Selección de técnicos que pueden atender citas.',
  'Selección de usuarios a los que se dirige la campaña.',
  'Revisión final antes de guardar.',
];

// Pasos en el formato que espera el Stepper de ui-v2 (id numérico + label).
const STEPPER_PASOS = PASOS.map((p) => ({ id: p.num, label: p.titulo }));

export default function CrearCampana() {
  const navigate = useNavigate();
  const { id: idParam } = useParams();
  const idInicial = idParam ? parseInt(idParam, 10) : null;

  const [state, dispatch] = useReducer(wizardReducer, initialState);
  const [precargando, setPrecargando] = useState(!!idInicial);
  const [precargaError, setPrecargaError] = useState(null);
  const [mensajeGuardado, setMensajeGuardado] = useState(null);

  useEffect(() => {
    if (!idInicial) return;
    setPrecargando(true);
    getCampanaById(idInicial)
      .then((c) => {
        if (c.estado !== 'Borrador') {
          navigate(`/citas-tecnicas/campanas/${c.id}/editar`, { replace: true });
          return;
        }
        dispatch({ type: 'LOAD', campana: c });
      })
      .catch((e) => {
        console.error('[precarga]', e);
        setPrecargaError(
          e?.response?.status === 404
            ? 'La campaña no existe o ya no es accesible.'
            : 'No se pudo cargar el borrador.'
        );
      })
      .finally(() => setPrecargando(false));
    // eslint-disable-next-line
  }, [idInicial]);

  const guardar = useCallback(
    async ({ comoActiva = false } = {}) => {
      dispatch({ type: 'SET_GUARDANDO', value: true });
      dispatch({ type: 'SET_ERRORES', errores: [] });
      try {
        const payload = buildPayload(state, {
          estado: comoActiva ? 'Activa' : 'Borrador',
        });

        let nuevoId = state.id;
        if (!nuevoId) {
          const res = await createCampana(payload);
          nuevoId = res.id;
          dispatch({ type: 'SET_ID', id: nuevoId });
          dispatch({ type: 'SUBTAREAS_IDS', subtareas: res.subtareas });
          navigate(`/citas-tecnicas/campanas/${nuevoId}/wizard`, { replace: true });
        } else {
          const res = await updateCampana(nuevoId, payload);
          dispatch({ type: 'SUBTAREAS_IDS', subtareas: res?.subtareas });
          if (comoActiva) {
            await cambiarEstadoCampana(nuevoId, 'Activa');
          }
        }

        if (comoActiva) {
          navigate(`/citas-tecnicas/campanas/${nuevoId}/resumen`);
        } else {
          setMensajeGuardado('Borrador guardado.');
          setTimeout(() => setMensajeGuardado(null), 2500);
        }
        return true;
      } catch (e) {
        console.error('[guardar]', e);
        const errs = e?.response?.data?.errores;
        if (Array.isArray(errs) && errs.length) {
          dispatch({ type: 'SET_ERRORES', errores: errs });
          saltarAPasoDeError(errs[0]?.campo, dispatch);
        } else {
          dispatch({
            type: 'SET_ERRORES',
            errores: [{
              campo: '_general',
              mensaje: e?.response?.data?.error || 'No se pudo guardar.',
            }],
          });
        }
        return false;
      } finally {
        dispatch({ type: 'SET_GUARDANDO', value: false });
      }
    },
    [state, navigate]
  );

  function siguiente() {
    if (!pasoEsValidoLocal(state.paso, state.payload)) return;
    dispatch({ type: 'SET_PASO', paso: state.paso + 1 });
  }
  function atras() {
    dispatch({ type: 'SET_PASO', paso: state.paso - 1 });
  }

  if (precargando) {
    return (
      <div className="px-6 py-12 text-center text-slate-500 text-sm">
        <Loader2 className="w-5 h-5 inline-block animate-spin mr-2" />
        Cargando borrador…
      </div>
    );
  }
  if (precargaError) {
    return (
      <div className="px-6 py-12 max-w-xl mx-auto">
        <div className="border border-red-200 bg-red-50 rounded-lg p-4 text-red-700 text-sm">
          {precargaError}
        </div>
        <div className="mt-4">
          <Button variant="outline" onClick={() => navigate('/citas-tecnicas/campanas')}>
            Volver a la lista
          </Button>
        </div>
      </div>
    );
  }

  const pasoActual = PASOS.find((p) => p.num === state.paso);
  const valido = pasoEsValidoLocal(state.paso, state.payload);
  const esUltimo = state.paso === TOTAL_PASOS;
  const errorGeneral = state.errores.find((e) => e.campo === '_general');

  return (
    // Rediseño (Fase 3): la barra de pasos va FUERA de la tarjeta (su overflow-hidden impedía fijarla)
    // y fija al pie de la página, como la de Editar. Al ser sticky ocupa su sitio en el flujo: al final
    // del contenido no tapa nada.
    <div className="min-h-full">
    <div className="px-4 py-6 sm:px-6">
      {/* Cabecera: atrás + título + subtítulo + Guardar borrador */}
      <PageHeader
        title={state.id ? 'Continuar borrador' : 'Nueva campaña'}
        subtitle="Configura la campaña paso a paso"
        onBack={() => navigate('/citas-tecnicas/campanas')}
        action={
          <div className="flex items-center gap-2">
            {mensajeGuardado && (
              <span className="text-xs text-emerald-700 flex items-center gap-1">
                <Check className="w-3.5 h-3.5" />
                {mensajeGuardado}
              </span>
            )}
            <Button
              variant="outline"
              onClick={() => guardar({ comoActiva: false })}
              disabled={state.guardando || !state.payload.titulo}
              title={!state.payload.titulo ? 'Pon un título para guardar' : ''}
            >
              {state.guardando ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Guardar borrador
            </Button>
          </div>
        }
      />

      {/* Stepper con líneas conectoras */}
      <div className="mt-8 overflow-x-auto">
        <Stepper
          steps={STEPPER_PASOS}
          activeId={state.paso}
          onStepClick={(id) => {
            // Solo se permite volver a pasos ya visitados (o el actual).
            if (id <= state.paso) dispatch({ type: 'SET_PASO', paso: id });
          }}
        />
      </div>

      {/* Card del contenido con respiración generosa + footer */}
      <Card className="mt-8 p-0 overflow-hidden border-slate-200">
        <div className="p-5 sm:p-8 md:p-10">
          <div className="mb-6">
            <h2 className="text-base font-semibold text-slate-900">
              {state.paso} · {pasoActual.titulo}
            </h2>
            <p className="text-sm text-slate-500 mt-1">
              {DESCRIPCION_PASO[state.paso - 1]}
            </p>
          </div>

          <div className="space-y-6">
            <ContenidoPaso
              paso={state.paso}
              payload={state.payload}
              errores={state.errores}
              dispatch={dispatch}
            />
          </div>

          {errorGeneral && (
            <div className="mt-6 border border-red-200 bg-red-50 rounded-lg p-3 text-sm text-red-700 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
              {errorGeneral.mensaje}
            </div>
          )}
        </div>
      </Card>
    </div>

      <div className="sticky bottom-0 z-20 border-t border-v25-line bg-white/95 backdrop-blur" data-barra-pasos>
        <WizardFooter
          className="flex-wrap gap-y-2 border-t-0 px-4 py-3 sm:px-6"
          step={state.paso}
          total={TOTAL_PASOS}
          left={
            <button
              type="button"
              onClick={() => navigate('/citas-tecnicas/campanas')}
              disabled={state.guardando}
              className="text-sm text-slate-500 hover:text-slate-900 disabled:opacity-50"
            >
              Cancelar
            </button>
          }
          right={
            <>
              {state.paso > 1 && (
                <Button variant="outline" onClick={atras} disabled={state.guardando}>
                  <ArrowLeft className="w-3.5 h-3.5" />
                  Atrás
                </Button>
              )}
              {!esUltimo ? (
                <Button onClick={siguiente} disabled={!valido || state.guardando}>
                  Siguiente
                  <ArrowRight className="w-3.5 h-3.5" />
                </Button>
              ) : (
                <Button onClick={() => guardar({ comoActiva: true })} disabled={state.guardando}>
                  {state.guardando ? <Loader2 className="w-4 h-4 animate-spin" /> : <Rocket className="w-4 h-4" />}
                  Crear y activar
                </Button>
              )}
            </>
          }
        />
      </div>
    </div>
  );
}

/**
 * Rediseño (Fase 3): cada paso con la disposición de Editar y a todo el ancho. Mismos componentes y
 * mismo dispatch; solo cambia cómo se reparten. Los recordatorios van en el paso 1, con las reglas
 * (como en Editar), y ya no en Fechas y horario.
 */
function ContenidoPaso({ paso, payload, errores, dispatch }) {
  const props = { payload, errores, dispatch };
  switch (paso) {
    case 1:
      return (
        <div className="space-y-8">
          <Paso1Info parte="ambito" {...props} />
          <BloquePaso titulo="Información">
            <InformacionDosColumnas autoFocusTitulo {...props} />
          </BloquePaso>
          <BloquePaso titulo="Reglas">
            <BloqueReglas {...props} />
          </BloquePaso>
        </div>
      );
    case 2: return <Paso2Subtareas {...props} />;
    case 3: return <Paso3Fechas {...props} sinRecordatorios />;
    case 4: return <Paso4DiasDisponibles {...props} />;
    case 5: return <Paso5Tecnicos dosColumnas {...props} />;
    case 6: return <Paso6Usuarios dosColumnas {...props} />;
    case 7:
      return (
        <Paso7Verificacion
          {...props}
          // Todos los pasos anteriores ya se han visto: volver a cualquiera, como en el indicador.
          irAPaso={(p) => dispatch({ type: 'SET_PASO', paso: p })}
        />
      );
    default: return null;
  }
}

function BloquePaso({ titulo, children }) {
  return (
    <section className="border-t border-v25-line pt-6">
      <h3 className="mb-4 text-sm font-semibold text-v25-ink-900">{titulo}</h3>
      {children}
    </section>
  );
}

function saltarAPasoDeError(campo, dispatch) {
  const MAPA = {
    titulo: 1, descripcion: 1, asignacion_modo: 1, max_cancelaciones_default: 1,
    // Fase 3: los recordatorios se rellenan ahora en el paso 1.
    recordatorio_1_dias_lab_antes: 1, recordatorio_2_dias_lab_antes: 1,
    subtareas: 2,
    fecha_inicio: 3, fecha_limite: 3,
    horario_inicio: 3, horario_fin: 3, duracion_cita: 3,
    tecnicos_ids: 5,
    usuarios_ids: 6,
  };
  const p = MAPA[campo];
  if (p) dispatch({ type: 'SET_PASO', paso: p });
}
